import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  Target,
  BarChart3,
  BookOpen,
  Calculator,
  Trophy,
  ScrollText,
  AlertTriangle,
  LogOut,
  Plug,
  LineChart,
  ChevronLeft,
  ChevronRight,
  Settings,
} from "lucide-react";
import DemonEye from "@/components/DemonEye";
import { useAuth } from "@/context/AuthContext";
import { fmtUSD } from "@/lib/calc";
import { useChallenge } from "@/context/ChallengeContext";
import InstallPWA from "@/components/InstallPWA";
import FloatingTelegram from "@/components/FloatingTelegram";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard-link" },
  { to: "/challenge", label: "Challenge", icon: CalendarDays, tid: "nav-challenge-link" },
  { to: "/broker", label: "Broker · MT5", icon: Plug, tid: "nav-broker-link" },
  { to: "/terminal", label: "Terminal", icon: LineChart, tid: "nav-terminal-link" },
  { to: "/goals", label: "Cele", icon: Target, tid: "nav-goals-link" },
  { to: "/stats", label: "Statystyki", icon: BarChart3, tid: "nav-stats-link" },
  { to: "/journal", label: "Trading Journal", icon: BookOpen, tid: "nav-journal-link" },
  { to: "/calculator", label: "Kalkulator lota", icon: Calculator, tid: "nav-calculator-link" },
  { to: "/milestones", label: "Milestones", icon: Trophy, tid: "nav-milestones-link" },
  { to: "/rules", label: "Zasady", icon: ScrollText, tid: "nav-rules-link" },
  { to: "/disclaimer", label: "Disclaimer", icon: AlertTriangle, tid: "nav-disclaimer-link" },
  { to: "/settings", label: "Ustawienia", icon: Settings, tid: "nav-settings-link" },
];

const MOBILE_NAV = NAV.filter((n) =>
  ["/dashboard", "/terminal", "/challenge", "/calculator", "/broker"].includes(n.to)
);

export default function Layout({ children }) {
  const { logout } = useAuth();
  const { data } = useChallenge();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const balance = data?.current_balance ?? 350;

  return (
    <div className="demon-bg min-h-screen text-demon-silver">
      <div className="grid-overlay fixed inset-0 pointer-events-none opacity-40" />

      {/* Desktop sidebar */}
      <aside
        className={`fixed left-0 top-0 bottom-0 ${collapsed ? "w-20" : "w-64"} hidden lg:flex flex-col glass border-r border-violet-900/30 z-40 p-3 transition-all duration-300`}
      >
        <button
          onClick={() => setCollapsed((c) => !c)}
          data-testid="sidebar-toggle"
          className="absolute -right-3 top-6 w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center shadow-[0_0_10px_rgba(168,85,247,0.5)] z-50 hover:bg-violet-500 transition"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        <div className={`flex items-center gap-3 mb-8 mt-1 ${collapsed ? "justify-center" : "px-2"}`}>
          <DemonEye size={collapsed ? 34 : 44} className="animate-eye" />
          {!collapsed && (
            <div>
              <div className="font-display font-extrabold text-base tracking-tight leading-none text-glow">
                THE MONEY CLUB
              </div>
              <div className="text-[9px] tracking-[0.3em] text-demon-muted mt-1">TRADING ACADEMY</div>
            </div>
          )}
        </div>

        {!collapsed ? (
          <div className="glow-card rounded-xl px-4 py-3 mb-6 mx-1">
            <div className="text-[10px] tracking-widest text-demon-muted">AKTUALNY KAPITAŁ</div>
            <div className="font-mono text-xl font-bold text-glow" data-testid="sidebar-balance">
              {fmtUSD(balance)}
            </div>
          </div>
        ) : (
          <div className="text-center mb-5 font-mono text-[11px] font-bold text-glow" data-testid="sidebar-balance">
            {fmtUSD(balance)}
          </div>
        )}

        <nav className="flex-1 space-y-1 overflow-y-auto no-scrollbar">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              title={n.label}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-violet-600/20 text-white border border-violet-500/40 shadow-[0_0_14px_rgba(168,85,247,0.25)]"
                    : "text-demon-muted hover:text-white hover:bg-violet-600/10"
                }`
              }
            >
              <n.icon size={18} />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          ))}
        </nav>

        <button
          onClick={handleLogout}
          data-testid="logout-button"
          title="Wyloguj"
          className={`mt-4 flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm text-red-300 hover:bg-red-500/10 transition-all`}
        >
          <LogOut size={18} />
          {!collapsed && <span>Wyloguj</span>}
        </button>
        {!collapsed && (
          <div className="mt-2">
            <InstallPWA variant="block" />
          </div>
        )}
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-16 glass border-b border-violet-900/40 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <DemonEye size={32} className="animate-eye" />
          <div>
            <div className="font-display font-bold text-sm leading-none text-glow">THE MONEY CLUB</div>
            <div className="text-[8px] tracking-[0.25em] text-demon-muted mt-0.5">TRADING ACADEMY</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="font-mono text-sm font-bold text-glow">{fmtUSD(balance)}</div>
          <button onClick={handleLogout} data-testid="logout-button-mobile" className="text-red-300">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Content */}
      <main
        className={`${collapsed ? "lg:pl-20" : "lg:pl-64"} pt-20 pb-28 lg:pb-12 px-4 sm:px-6 lg:px-10 max-w-7xl mx-auto min-h-screen relative z-10 transition-all duration-300`}
      >
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 h-16 lg:hidden flex items-center justify-around glass border-t border-violet-900/40 z-50 px-2">
        {MOBILE_NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`${n.tid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-2 py-1 rounded-lg text-[10px] transition-all ${
                isActive ? "text-violet-300" : "text-demon-muted"
              }`
            }
          >
            <n.icon size={20} />
            <span>{n.label.split(" ")[0]}</span>
          </NavLink>
        ))}
      </nav>

      <FloatingTelegram />
    </div>
  );
}
